'use client';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useState, useDeferredValue, useRef, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fetchPlaceOfDeliveries } from '@eliadmin/core/src/api/places-of-delivery/places-of-delivery-api';
import {
  normalizeText,
  type PlaceOfDeliveryLocal,
} from '../cart/detail/shipment-types/cart-shipment-place-dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Button } from '@eliadmin/core/src/components/ui/button';

export interface SuppliersMapProps {
  onSupplierSelect?: (supplier: PlaceOfDeliveryLocal) => void;
  showCloseButton?: boolean;
}

export const SuppliersMap = ({ onSupplierSelect, showCloseButton = false }: SuppliersMapProps) => {
  const locale = useLocaleStore(state => state.currentLocale);
  const { t } = useTranslations();
  const { 'google-api': googleApiKey } = useGlobalStore(state => state.apiKeys);

  // Fetch suppliers data
  const { data: place_of_deliveries } = useQuery({
    queryKey: [CacheKey.PLACE_OF_DELIVERY, locale],
    queryFn: async () => {
      return fetchPlaceOfDeliveries({
        locale,
        query: { results: 9999 },
      })
        .then(res => res.data)
        .catch(() => []);
    },
  });

  // States for highlighting, selection, filtering, etc.
  const [highlightedPlace, setHighlightedPlace] = useState<number | string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceOfDeliveryLocal | null>(null);
  const [placesOfDeliveries, setPlacesOfDeliveries] = useState<PlaceOfDeliveryLocal[]>([]);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [showList, setShowList] = useState(false);
  const [itemsToShow, setItemsToShow] = useState(10);

  // Adjust itemsToShow based on viewport width
  useEffect(() => {
    const updateItemsToShow = () => {
      if (window.innerWidth >= 768) {
        setItemsToShow(20);
      } else {
        setItemsToShow(10);
      }
    };
    updateItemsToShow();
    window.addEventListener('resize', updateItemsToShow);
    return () => window.removeEventListener('resize', updateItemsToShow);
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey?.token ?? '',
  });

  const mobileListRef = useRef<HTMLDivElement>(null);
  const desktopListRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [zoom, setZoom] = useState(10);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const getDaysText = (days: number) => {
    if (days === 1) return t('text1976');
    if (days > 1 && days < 5) return t('text1977');
    return t('text1978');
  };

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      map.setZoom(zoom);
      map.setOptions({
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
      });
      setMap(map);
    },
    [zoom],
  );

  // Set initial center (or default) based on first supplier
  const initialCenter = {
    lat: place_of_deliveries?.[0]?.latitude ? +place_of_deliveries[0].latitude : 48.13881165313404,
    lng: place_of_deliveries?.[0]?.longitude
      ? +place_of_deliveries[0].longitude
      : 17.118919055143596,
  };

  const [center, setCenter] = useState(initialCenter);

  const setMapCenter = (newCenter: google.maps.LatLngLiteral) => {
    if (map) {
      map.panTo(newCenter);
    } else {
      setCenter(newCenter);
    }
  };

  // Custom scroll function to center the map container in viewport
  const scrollMapIntoView = () => {
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      const absoluteElementTop = rect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2 + rect.height / 2;
      window.scrollTo({
        top: middle,
        behavior: 'smooth',
      });
    }
  };

  // Compute filtered suppliers (pure computation, no side effects)
  const computedFilteredPlaces = useMemo(() => {
    return (place_of_deliveries || []).filter(point => {
      return normalizeText(
        point.name + point.address + point.street_number + point.city + point.postcode,
      ).includes(normalizeText(deferredQuery));
    });
  }, [deferredQuery, place_of_deliveries]);

  // Update state when filtered suppliers change
  useEffect(() => {
    setPlacesOfDeliveries(computedFilteredPlaces);
    if (computedFilteredPlaces.length > 0) {
      // If the current highlighted supplier isn't in the filtered list, update it.
      if (!computedFilteredPlaces.some(p => p.id === highlightedPlace)) {
        setHighlightedPlace(computedFilteredPlaces[0].id);
        setMapCenter({
          lat: +computedFilteredPlaces[0].latitude,
          lng: +computedFilteredPlaces[0].longitude,
        });
      }
    } else {
      setHighlightedPlace(null);
    }
  }, [computedFilteredPlaces, highlightedPlace]);

  // Handle selection from either list or marker click.
  // If an onSupplierSelect prop is provided, call it with the selected supplier.
  const handleSelectSupplier = (supplier: PlaceOfDeliveryLocal) => {
    setSelectedPlace(prev => {
      if (!prev || prev.id !== supplier.id) {
        return supplier;
      }
      return prev;
    });

    setHighlightedPlace(supplier.id);

    setTimeout(() => {
      setMapCenter({ lat: +supplier.latitude, lng: +supplier.longitude });
      setZoom(13);
    }, 100);
  };

  return (
    <div className="flex max-h-[600px] flex-col overflow-hidden md:flex-row">
      {/* Mobile: Toggleable Suppliers List */}
      <div className="p-4 md:hidden">
        <Input
          type="text"
          placeholder={t('text871')}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div className="flex items-center justify-between">
          <button
            className="mt-4 flex items-center whitespace-nowrap rounded-sm bg-gray-200 px-4 py-2 transition-all duration-300"
            onClick={() => setShowList(prev => !prev)}
          >
            {showList ? t('text1984') : t('text1985')}
            <svg
              className={`ml-2 h-4 w-4 transition-transform duration-300 ${
                showList ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {showCloseButton && (
            <div className="flex justify-end p-4">
              <Button
                className="rounded-sm bg-blue-500 px-4 py-2 text-white transition-all duration-300 disabled:opacity-50"
                disabled={!selectedPlace}
                onClick={() => {
                  if (selectedPlace && onSupplierSelect) {
                    onSupplierSelect(selectedPlace);
                  }
                }}
              >
                {t('Vybrať dodávateľa')}
              </Button>
            </div>
          )}
        </div>
        {showList && (
          <div
            ref={mobileListRef}
            className="mt-4 h-[400px] overflow-auto rounded-sm border p-2 transition-all duration-300"
          >
            {computedFilteredPlaces.length > 0
              ? computedFilteredPlaces.slice(0, itemsToShow).map(point => (
                  <div
                    key={point.id}
                    data-delivery-point-id={point.id}
                    onClick={() => {
                      handleSelectSupplier(point);
                      setShowList(false);
                      if (window.innerWidth < 768) {
                        scrollMapIntoView();
                      }
                    }}
                    className={`cursor-pointer  border-b border-border p-2 transition-all duration-300 hover:bg-gray-100 ${
                      highlightedPlace === point.id ? 'bg-gray-100' : ''
                    }`}
                  >
                    <p className="text-sm font-semibold">{point.name}</p>
                    <p className="text-sm">
                      {point.address} {point.street_number}, {point.city}
                    </p>
                    <p className="text-sm">
                      {point.postcode}, {point.country?.name}
                    </p>
                  </div>
                ))
              : t('text2')}
          </div>
        )}
      </div>

      {/* Desktop: Suppliers List always visible on the left */}
      <div
        className="hidden overflow-auto p-4 md:block md:max-h-[600px]"
        style={{
          minWidth: '350px',
        }}
      >
        <Input
          type="text"
          placeholder={t('text871')}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div
          ref={desktopListRef}
          className="mt-4 h-[600px] max-h-[700px] overflow-auto rounded-sm border p-2 transition-all duration-300"
        >
          {computedFilteredPlaces.length > 0
            ? computedFilteredPlaces.slice(0, itemsToShow).map(point => (
                <div
                  key={point.id}
                  data-delivery-point-id={point.id}
                  onClick={() => handleSelectSupplier(point)}
                  className={`cursor-pointer border-b border-border p-2 transition-all duration-300 hover:bg-gray-100 ${
                    highlightedPlace === point.id ? 'bg-gray-100' : ''
                  }`}
                >
                  <p className="text-sm font-semibold">{point.name}</p>
                  <p className="text-sm">
                    {point.address} {point.street_number}, {point.city}
                  </p>
                  <p className="text-sm">
                    {point.postcode}, {point.country?.name}
                  </p>
                </div>
              ))
            : t('')}
        </div>
      </div>

      {/* Map Container */}
      <div className="w-full md:w-2/3">
        <div ref={mapContainerRef} className="h-[700px]">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              zoom={zoom}
              center={center}
              onLoad={onLoad}
              onUnmount={onUnmount}
              clickableIcons={false}
            >
              {placesOfDeliveries.map(point => (
                <Marker
                  key={point.id}
                  position={{ lat: +point.latitude, lng: +point.longitude }}
                  opacity={highlightedPlace === null ? 1 : highlightedPlace === point.id ? 1 : 0.5}
                  onClick={() => handleSelectSupplier(point)}
                />
              ))}
              {selectedPlace && (
                <InfoWindow
                  position={{
                    lat: +selectedPlace.latitude + 0.0002,
                    lng: +selectedPlace.longitude,
                  }}
                  onCloseClick={() => setSelectedPlace(null)}
                >
                  <div style={{ backgroundColor: 'white', padding: '10px' }}>
                    <h4 className="font-bold">{selectedPlace.name}</h4>
                    <p className="text-sm">
                      {selectedPlace.address} {selectedPlace.street_number}, {selectedPlace.city},{' '}
                      {selectedPlace.postcode}
                    </p>
                    <p className="text-sm">
                      {t('text237')}: {selectedPlace.availability_in_days}{' '}
                      {getDaysText(selectedPlace.availability_in_days)}
                    </p>
                    <p className="whitespace-pre-wrap text-sm">{selectedPlace.opening_hours}</p>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : null}
        </div>
        {/* Conditionally render the close/Select button if showCloseButton prop is true */}
        {showCloseButton && (
          <div className="flex justify-end p-4">
            <Button
              className="rounded-sm bg-blue-500 px-4 py-2 text-white transition-all duration-300 disabled:opacity-50"
              disabled={!selectedPlace}
              onClick={() => {
                if (selectedPlace && onSupplierSelect) {
                  onSupplierSelect(selectedPlace);
                }
              }}
            >
              {t('Vybrať dodávateľa')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
